import React from 'react'
import { Badge } from 'spa/member/components/Badge'
import { CardProps } from './types'
import { useFormatNumber } from 'spa/hooks/useFormatNumber'
import styles from './Card.module.scss'

export const StyledCard: React.FC<CardProps> = ({
  size = 'default',
  device = 'PC',
  image = '',
  alt = '',
  tags = [],
  contents = '',
  heading = '',
  subHeading = '',
  pricing = '',
  className = '',
  style = {},
  onClick,
}) => {

  const baseCardContainerStyle = {
    PC: size === 'lg' ? { width: '972px' } : { width: '225px' },
    TB: size === 'lg' ? { width: '688px' } : { width: '332px' },
    SP: size === 'lg' ? { width: '343px' } : { width: '343px' },
  }[device]

  const baseImageContainerStyle = {
    PC: size === 'lg' ? { width: '400px', height: '300px' } : { width: '225px', height: '112.5px' },
    TB: size === 'lg' ? { width: '300px', height: '225px' } : { width: '332px', height: '166px' },
    SP: size === 'lg' ? { width: '343px', height: '257px' } : { width: '140px', height: '105px' },
  }[device]

  const { formatNumber } = useFormatNumber()

  const getContainerClassName = (size: string, device: string): string => {
    if (size === 'lg') {
      if (device === 'SP') return `rounded-L flex-col`
      if (device === 'TB') return `rounded-L p-XL gap-XL flex-row`
      return `rounded-L p-3XL gap-3XL flex-row`
    }

    if (device === 'SP') return `items-center rounded-M py-XS pr-M pl-XS flex-row`
    return `rounded-M flex-col`
  }

  const baseImageContainerClassName = (size: string, device: string): string => {
    if (size === 'lg') {
      return device === 'SP' ? `rounded-t-M` : `rounded-M`
    } else if (['TB', 'PC'].includes(device)) {
      return `rounded-t-M`
    }

    if (device === 'SP') return `rounded-S`
    return `rounded-M`
  }

  const baseTextContainerClassName = (size: string, device: string): string => {
    if (size === 'lg') {
      if (device === 'SP') return `p-XL`
    }
    if (device === 'SP') return `p-XS pl-S gap-XS w-full`
    return `pt-M pb-2XL px-M gap-XS w-full`
  }

  return (
    <div
      className={`flex bg-member-white-100 ${getContainerClassName(size, device)} ${className} ${styles.card}`}
      style={{
        width: baseCardContainerStyle.width,
        ...style
      }}
      onClick={onClick}
    >
      {image && (
        <div
          className={`items-center justify-center flex-shrink-0 w-full h-full overflow-hidden ${baseImageContainerClassName(size, device)}`}
          style={{
            width: baseImageContainerStyle.width,
            height: baseImageContainerStyle.height,
          }}
        >
          <img
            src={image}
            alt={alt || ''}
            className={`object-cover box-content ${baseImageContainerClassName(size, device)} ${styles.image}`}
            style={{
              width: baseImageContainerStyle.width,
              height: baseImageContainerStyle.height,
            }}
          />
        </div>
      )}

      <div
        className={`flex flex-col self-start ${baseTextContainerClassName(size, device)}`}
      >
        {Array.isArray(tags) && tags.length !== 0 && (
          <div
            className="flex flex-row flex-wrap gap-2XS"
            style={{
              overflow: 'hidden',
            }}
          >
            {tags.map((tag, index) => (
              <Badge
                key={index}
                badgeType={size === 'lg' && device !== 'SP' ? 'default' : 'small'}
                kind="primary"
              >
                {tag}
              </Badge>
            ))}
          </div>
        )}
        {(heading || pricing) && (
          <div className="flex flex-col self-stretch">
            {heading && (
              <div
                className={`text-member-gray-100 font-bold ${
                  size === 'lg'
                    ? device === 'SP'
                      ? 'text-M pt-S pb-2XS'
                      : 'text-XL pt-M pb-XS'
                    : device === 'SP'
                    ? 'text-XS'
                    : 'text-S'
                } ${styles.heading}`}
                style={{
                  fontFeatureSettings: '"palt"',
                  lineHeight: '1.75',
                }}
              >
                {heading}
              </div>
            )}

            {size === 'lg' && subHeading && (
              <p
                className={`${device === 'SP' ? 'text-XS' : 'text-S'}`}
                style={{
                  lineHeight: '1.75',
                }}
              >
                {subHeading}
              </p>
            )}

            {pricing && !isNaN(Number(pricing)) && (
              <p style={{ fontFeatureSettings: '"palt"', lineHeight: 1 }}>
                <span
                  className={`font-bold text-member-red-100 ${
                    size === 'lg'
                      ? 'text-XL'
                      : device === 'TB'
                      ? 'text-L'
                      : device === 'SP'
                      ? 'text-S'
                      : 'text-M'
                  }`}
                  style={{
                    lineHeight: '1.75',
                  }}
                >
                  {formatNumber(pricing)}
                </span>
                <span
                  className="text-member-gray-100 font-normal text-2XS ml-2XS"
                  style={{
                    lineHeight: '1.75',
                  }}
                >
                  円/月
                </span>
                <span className={`text-member-gray-100 font-normal text-2XS ml-2XS ${styles.weeklyConversion}`}>（週5日換算）</span>
              </p>
            )}
          </div>
        )}

        {contents && (
          <p
            className={`text-member-gray-100 ${
              size === 'lg' ? device === 'SP' ? 'text-XS pt-M' :'text-S pt-M' : device === 'TB' ? 'text-S' : device === 'SP' ? 'text-2XS' : 'text-XS'
            }`}
            style={{
              lineHeight: '1.75',
            }}
          >
            {contents}
          </p>
        )}
      </div>
    </div>
  )
}
